import { useQuery } from '@apollo/client';
import { Pill, Table, TableColumn } from '@sixfold/common-ui';
import React from 'react';
import { Link } from 'react-router';

import {
  RelatedChildToursQuery,
  RelatedChildToursQueryVariables,
  TransportChainQuery,
  TransportChainQueryVariables,
} from '../../lib/graphql';
import { Loader } from '../../lib/loader';
import { Routes } from '../../routes';
import {
  Tour,
  TransportChainDetails,
  RelatedChildToursDetails,
  TransportChainTour,
  GroupedTransportChainTours,
} from '../entities';
import { transportChainQuery, relatedChildToursQuery } from '../graphql';
import { getStatusPillKind } from '../utils';

class TransportChainLoadingContainer extends Loader<
  TransportChainQuery,
  TransportChainDetails | null,
  TransportChainQueryVariables
> {}

class RelatedChildToursLoadingContainer extends Loader<
  RelatedChildToursQuery,
  RelatedChildToursDetails | null,
  RelatedChildToursQueryVariables
> {}

interface RelatedToursTableRow {
  tourId: number;
  transportNumber: string | null;
  status: string | null;
  visibilityServices: string[];
  containerNumber?: string | null;
  parentTourId?: string | null;
  isParent?: boolean;
  isCurrent: boolean;
}

interface RelatedToursTableGroup {
  groupId: string | null;
  groupLabel: string;
  tours: RelatedToursTableRow[];
}

const groupTransportChainTours = (
  transportChain: TransportChainDetails['transportChain'],
): GroupedTransportChainTours[] => {
  if (!transportChain || !transportChain.tours) {
    return [];
  }

  const groupMap = new Map<string | null, TransportChainTour[]>();

  transportChain.tours.forEach((chainTour) => {
    const groupKey = chainTour.group;
    if (!groupMap.has(groupKey)) {
      groupMap.set(groupKey, []);
    }
    const group = groupMap.get(groupKey);
    if (group) {
      group.push(chainTour);
    }
  });

  return Array.from(groupMap.entries()).map(([group, tours]) => ({
    group,
    tours,
  }));
};

const formatVisibilityServices = (visibilityServices: string[]): string => {
  const serviceMap: Record<string, string> = {
    VISIBILITY_OCEAN: 'Ocean',
    VISIBILITY_ROAD: 'Road',
    VISIBILITY_AIR: 'Air',
  };

  const readableServices = visibilityServices.map((service) => serviceMap[service] || service).filter(Boolean);

  return readableServices.length > 0 ? readableServices.join(', ') : '-';
};

type TourRow = {
  id: React.Key;
  tourId: number;
  transportNumber: string | null;
  status: string | null;
  parentTourId: string | null | undefined;
  visibility: string;
  isCurrent: boolean;
  isParent: boolean | undefined;
};

const buildColumnsWithoutParent = (): TableColumn<TourRow>[] => [
  {
    id: 'tourId',
    title: 'Tour ID',
    cellContent: ({ row }) => (
      <Link
        to={Routes.Tour.generatePath({ tour_id: row.tourId.toString() })}
        style={row.isCurrent ? { fontWeight: 'bold' } : {}}>
        {row.isParent && '👑 Parent '}Tour {row.tourId}
        {row.isCurrent && ' (Current)'}
      </Link>
    ),
  },
  {
    id: 'transportNumber',
    title: 'Transport Number',
    width: '40%',
    cellContent: ({ row }) => <>{row.transportNumber ?? '-'}</>,
  },
  {
    id: 'status',
    title: 'Status',
    cellContent: ({ row }) => <>{row.status && <Pill kind={getStatusPillKind(row.status)}>{row.status}</Pill>}</>,
  },
  { id: 'visibility', title: 'Visibility' },
];

const buildColumnsWithParent = (): TableColumn<TourRow>[] => [
  {
    id: 'tourId',
    title: 'Tour ID',
    cellContent: ({ row }) => (
      <Link
        to={Routes.Tour.generatePath({ tour_id: row.tourId.toString() })}
        style={row.isCurrent ? { fontWeight: 'bold' } : {}}>
        {row.isParent && '👑 Parent '}Tour {row.tourId}
        {row.isCurrent && ' (Current)'}
      </Link>
    ),
  },
  {
    id: 'transportNumber',
    title: 'Transport Number',
    width: '40%',
    cellContent: ({ row }) => <>{row.transportNumber ?? '-'}</>,
  },
  {
    id: 'status',
    title: 'Status',
    cellContent: ({ row }) => <>{row.status && <Pill kind={getStatusPillKind(row.status)}>{row.status}</Pill>}</>,
  },
  {
    id: 'parentTourId',
    title: 'Parent Tour',
    cellContent: ({ row }) =>
      row.parentTourId ? (
        <Link to={Routes.Tour.generatePath({ tour_id: row.parentTourId })}>Tour {row.parentTourId}</Link>
      ) : (
        '-'
      ),
  },
  { id: 'visibility', title: 'Visibility' },
];

const RelatedToursTable: React.FunctionComponent<{
  title: string;
  groups: RelatedToursTableGroup[];
  hasParentColumn: boolean;
}> = ({ title, groups, hasParentColumn }) => {
  const hasData = groups.length > 0 && groups.some((group) => group.tours.length > 0);
  const columns = hasParentColumn ? buildColumnsWithParent() : buildColumnsWithoutParent();

  const hasMultipleGroups = new Set(groups.map((g) => g.groupId)).size > 1;

  return (
    <div>
      <h3>{title}</h3>
      {!hasData ? (
        <p>No related tours</p>
      ) : hasMultipleGroups ? (
        groups.map((group, groupIndex) => {
          const groupRows: TourRow[] = group.tours.map((tourRow) => ({
            id: tourRow.tourId,
            tourId: tourRow.tourId,
            transportNumber: tourRow.transportNumber,
            status: tourRow.status,
            parentTourId: tourRow.parentTourId,
            visibility: formatVisibilityServices(tourRow.visibilityServices || []),
            isCurrent: tourRow.isCurrent,
            isParent: tourRow.isParent,
          }));

          const label = group.groupId ? group.groupLabel : 'No Container Assignment';

          return (
            <div key={groupIndex}>
              <div
                style={{
                  backgroundColor: 'var(--color-neutral-bg-surface-sunken)',
                  fontWeight: 'bold',
                  padding: '10px',
                  marginBottom: '4px',
                }}>
                {label}
              </div>
              <Table data={groupRows} columns={columns} className="compact-table" />
            </div>
          );
        })
      ) : (
        <Table
          data={groups.flatMap((group) =>
            group.tours.map((tourRow) => ({
              id: tourRow.tourId,
              tourId: tourRow.tourId,
              transportNumber: tourRow.transportNumber,
              status: tourRow.status,
              parentTourId: tourRow.parentTourId,
              visibility: formatVisibilityServices(tourRow.visibilityServices || []),
              isCurrent: tourRow.isCurrent,
              isParent: tourRow.isParent,
            })),
          )}
          columns={columns}
          className="compact-table"
        />
      )}
    </div>
  );
};

export const TourTransportChainTab: React.FunctionComponent<{ tour: Tour }> = ({ tour }) => {
  const result = useQuery<TransportChainQuery, TransportChainQueryVariables>(transportChainQuery, {
    variables: { tour_id: tour.tour_id },
    fetchPolicy: 'no-cache',
  });

  return (
    <TransportChainLoadingContainer
      result={result}
      mapData={({ data }) => {
        const { tour } = data;
        return {
          tour_id: tour?.tour_id,
          transportMode: tour?.transportMode,
          transportChain: tour?.transportChain,
        } as TransportChainDetails;
      }}>
      {(transportChainDetails) => {
        const groupedTours = groupTransportChainTours(transportChainDetails?.transportChain || null);
        const hasParentTours =
          transportChainDetails?.transportChain?.tours?.some((chainTour) => chainTour.parentTourId) || false;

        const hasMultipleContainers = groupedTours.filter((group) => group.group).length > 1;

        const groups: RelatedToursTableGroup[] = groupedTours.map((group) => ({
          groupId: group.group,
          groupLabel: group.group ? `Container ID: ${group.group}` : '',
          tours: group.tours.map((chainTour) => ({
            tourId: chainTour.tour.tour_id,
            transportNumber: chainTour.tour.shipper_transport_number,
            status: chainTour.tour.status,
            visibilityServices: chainTour.tour.visibilityServices || [],
            parentTourId: chainTour.parentTourId,
            isParent: !chainTour.parentTourId && chainTour.tour.transportMode === 'Ocean' && hasMultipleContainers,
            isCurrent: chainTour.tour.tour_id === tour.tour_id,
          })),
        }));

        return (
          <RelatedToursTable title="Related Tours (Transport Chain)" groups={groups} hasParentColumn={hasParentTours} />
        );
      }}
    </TransportChainLoadingContainer>
  );
};

export const TourRelatedChildToursTab: React.FunctionComponent<{ tour: Tour }> = ({ tour }) => {
  const result = useQuery<RelatedChildToursQuery, RelatedChildToursQueryVariables>(relatedChildToursQuery, {
    variables: { tour_id: tour.tour_id },
    fetchPolicy: 'no-cache',
  });

  return (
    <RelatedChildToursLoadingContainer
      result={result}
      mapData={({ data }) => {
        const { tour } = data;
        return {
          tour_id: tour?.tour_id,
          transportMode: tour?.transportMode,
          relatedChildTours: tour?.relatedChildTours,
        } as RelatedChildToursDetails;
      }}>
      {(relatedChildToursDetails) => {
        const relatedTours = relatedChildToursDetails?.relatedChildTours || [];

        const getContainerNumber = (tourData: { containerNumber?: string | null }) => {
          return tourData.containerNumber || null;
        };

        const groups: RelatedToursTableGroup[] = relatedTours.map((relatedTour) => ({
          groupId: getContainerNumber(relatedTour),
          groupLabel: `Container ID: ${getContainerNumber(relatedTour)}`,
          tours: [
            {
              tourId: relatedTour.tour_id,
              transportNumber: relatedTour.shipper_transport_number,
              status: relatedTour.status,
              visibilityServices: relatedTour.visibilityServices || [],
              parentTourId: relatedTour.parent_tour_id,
              isParent: !relatedTour.parent_tour_id,
              isCurrent: relatedTour.tour_id === tour.tour_id,
            },
          ],
        }));

        return <RelatedToursTable title="Related Tours (Parent/Child)" groups={groups} hasParentColumn={false} />;
      }}
    </RelatedChildToursLoadingContainer>
  );
};
